import {
  DocumentType,
  PitbullCountry,
  StepEndpointSecurities,
  StepSevenSecurities,
  Template,
} from "../../types";
import {
  firestoreCollectionByDocumentType,
  firestoreDocumentByDocumentType,
  getCitizenshipOptions,
  getCountries,
  getCountriesOptions,
} from "../../utils";
import axios from "axios";
import StepThree from "../../steps/securities/stepThree";
import StepTwo from "../../steps/securities/stepTwo";
import StepOne from "../../steps/securities/stepOne";
import PrevStep from "../../steps/securities/prevStep";
import StepFour from "../../steps/securities/stepFour";
import StepFive from "../../steps/securities/stepFive";
import { preconfirmationSteps } from "../../steps/securities/preconfirmationSteps";
import {
  onUpdateCompletedForm,
  onUpdateHolder,
  addSendedStep,
} from "../../reducers/securitiesReducer";
import PreconfirmationHeader from "../../components/SecuritiesPreconfirmationHeader/SecuritiesPreconfirmationHeader";
import { getHoldersInformation } from "../../utils/securities";
import {
  CreateDraftProps,
  sendStep,
  completeDraft,
} from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { FormikValues } from "formik";
import { AppDispatch, RootState } from "../../app/store";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import {
  captureSecuritiesSubmitAttempt,
  captureSecuritiesError,
  addStepBreadcrumb,
} from "../../sentry/utils";
import * as Sentry from "@sentry/react";
import { getCompanyData } from "../../utils/companies";

const onboardingSecurities = async ({
  values,
  dispatch,
  state,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
}) => {
  const { favicon, formTitle, logo } = getCompanyData();
  const [countries] = await axios.all([getCountries()]);

  const {
    accountHolders,
    language,
    currentHolder,
    beneficiariesCount,
    holdersData,
  } = state.securities;

  const countriesTyped = (countries as PitbullCountry[]) ?? [];

  const countriesOptions = getCountriesOptions(countriesTyped);

  const citizenshipOptions = getCitizenshipOptions(countriesTyped);

  const db = getFirestore();

  const saveFileToFirestore = (fileName: string, type: DocumentType) => {
    const docRef = doc(
      db,
      firestoreCollectionByDocumentType[type],
      state.securities.draftUUID,
    );
    setDoc(
      docRef,
      {
        [firestoreDocumentByDocumentType[type]]: fileName,
        ...(type.includes("DNI") ? { scannedQR: false } : {}),
      },
      { merge: true },
    );
  };

  const template: Template = {
    favicon,
    formTitle,
    logo,
    onboardingType: "SECURITIES",
    preconfirmation: () => ({
      headerContent: <PreconfirmationHeader />,
      steps: [],
      tabs: Array.from({ length: accountHolders }, (_, i) => ({
        label: `Titular ${i + 1}`,
        steps: preconfirmationSteps({
          values,
          dispatch,
          state,
          countriesOptions,
          citizenshipOptions,
          currentHolder: i + 1,
          holdersData,
        }),
      })),
      defaultSelectedKey: ({ state }) => {
        const { currentHolder } = state.securities;
        return (currentHolder - 1).toString();
      },
      disabledKeys: ({ state }) => {
        const { holdersData } = state.securities;
        // disable holders tabs that haven't been completed (there is no data in state)
        return Array.from({ length: accountHolders }, (_, i) => {
          const holderData = holdersData.find(
            (holder) => holder.currentHolder === i + 1,
          );
          if (!holderData) {
            return i.toString();
          }
        }).filter((key) => key !== undefined);
      },
      onChangeTab: ({ key, dispatch, state }) => {
        const currentTab = Number(key) + 1;

        if (currentTab !== state.securities.currentHolder) {
          dispatch(onUpdateHolder(currentTab));
        }
      },
      checkboxes: [],
      onSubmit: async ({ state, dispatch, navigate }) => {
        const { accountHolders, holdersData, initialValidationData } =
          state.securities;
        if (accountHolders > holdersData.length) {
          dispatch(onUpdateHolder(holdersData.length + 1));
          navigate("/securities");
        } else {
          addStepBreadcrumb(7, "enter", {
            action: "preconfirmation_submit",
            holdersCount: holdersData.length,
          });

          captureSecuritiesSubmitAttempt({ accountHolders, holdersData });

          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1,
          );
          //steps 1 to 6 are sent by the first holder, step 7 has all the additional holders information
          const sendedSteps = firstHolder?.sendedSteps || [];

          let holdersInformation;
          try {
            holdersInformation = getHoldersInformation({
              holdersData,
              language,
            });
          } catch (error) {
            captureSecuritiesError({
              error,
              stepId: 7,
              stepName: "Preconfirmation - getHoldersInformation",
              currentHolder: state.securities.currentHolder,
              requestInfo: { url: "getHoldersInformation", method: "LOCAL" },
              state,
            });
            throw error;
          }

          const step: StepSevenSecurities = {
            "@type": "7_SECURITY",
            holders: holdersInformation,
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "7_SECURITY",
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            cuit: initialValidationData.taxId,
            referrer: initialValidationData.referred,
            steps: [...previousSteps, step],
          };

          // Send step 7 with holders information
          try {
            await sendStep({ stepId: 7, body });
            dispatch(addSendedStep(step));
          } catch (error) {
            console.error("Error sending step 7:", error);
            captureSecuritiesError({
              error,
              stepId: 7,
              stepName: "Preconfirmation - Send Step 7 (Holders Info)",
              currentHolder: state.securities.currentHolder,
              // formValues,
              requestInfo: {
                url: `steps/7`,
                method: "POST",
                body: {
                  companyType: body.companyType,
                  personType: body.personType,
                  holdersCount: holdersData.length,
                },
              },
              state,
            });
            throw error;
          }

          // Complete the draft
          try {
            await completeDraft({ draftUUID: state.securities.draftUUID });
            dispatch(onUpdateCompletedForm(true));
          } catch (error) {
            console.error("Error completing draft:", error);
            captureSecuritiesError({
              error,
              stepId: 7,
              stepName: "Preconfirmation - Complete Draft",
              currentHolder: state.securities.currentHolder,
              // formValues,
              requestInfo: {
                url: `drafts/${state.securities.draftUUID}/complete`,
                method: "POST",
                body: { draftUUID: state.securities.draftUUID },
              },
              state,
            });
            throw error;
          }

          // Track successful completion in Sentry
          addStepBreadcrumb(7, "complete", {
            action: "onboarding_completed",
            holdersCount: holdersData.length,
          });
          Sentry.captureMessage(
            "Securities onboarding completed successfully",
            {
              level: "info",
              tags: {
                "onboarding.type": "SECURITIES",
                "onboarding.status": "completed",
              },
              extra: {
                holdersCount: holdersData.length,
                language: state.securities.language,
                draftUUID: state.securities.draftUUID,
              },
            },
          );

          dispatch({ type: "reset-store" });
          logAnalyticsAndHotjarEvent("onboarding_completed", {
            personType: "SECURITIES",
          });
          navigate(
            `/registro-enviado?language=${state.securities.language ?? "es"}`,
          );
        }
      },
      submitButtonText: ({ state }) => {
        const { accountHolders, holdersData } = state.securities;
        const allHoldersCompleted = holdersData.length === accountHolders;
        return allHoldersCompleted ? "Enviar" : "Guardar y Continuar";
      },
    }),
    steps: [
      PrevStep({ language, accountHolders, currentHolder }),
      StepOne({
        language,
        countriesOptions,
        currentHolder,
        holdersData,
        saveFileToFirestore,
      }),
      StepTwo({
        language,
        countriesOptions,
        citizenshipOptions,
        currentHolder,
        saveFileToFirestore,
      }),
      StepThree({ language, countriesOptions, currentHolder }),
      StepFour({ language, currentHolder }),
      ...(currentHolder === 1
        ? [StepFive({ language, countriesOptions, beneficiariesCount })]
        : []),
    ],
  };

  return template;
};

export default onboardingSecurities;
